import { useMemo, useState } from "react";
import { Plus, Trash2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  HorarioPatronDiasEditor,
  horarioPatronDiasDefecto,
  normalizarHorarioPatronDias,
  unificarTramoPatron,
} from "@/components/HorarioPatronDiasEditor";
import {
  DEFAULT_FLEXIBLE,
  DIAS_LAB_FLEXIBLE_ESTIMADO,
  fmtHorasDia,
  objetivoDiarioFlexible,
  objetivoDiarioInusual,
  MODO_FIJO,
  MODO_FLEXIBLE,
  MODO_PATRON_TURNOS,
  MODOS_HORARIO,
  PATRONES_TURNO,
  inicioAnioIso,
  metricasTurnoPatron,
} from "@/lib/horarioModos";

function genId() {
  return `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function addDaysIso(iso, days) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtDiaCorto(iso) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("es-ES", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
}

/** Vista previa: cada turno ocupa su propio bloque (N trabajo + M descanso). */
function bloquesPreview(patron) {
  const turnos = patron.turnos || [];
  let seq = normalizarSecuenciaRotacion(patron.secuencia_turnos, turnos, patron.dias_trabajo);
  if (!seq.length && turnos[0]) seq = [turnos[0].id];
  const byId = Object.fromEntries(turnos.map((t) => [t.id, t]));
  return seq.map((tid) => {
    const t = byId[tid];
    const met = metricasTurnoPatron(t || {}, patron);
    return {
      id: tid,
      nombre: t?.nombre || "Turno",
      ...met,
      longitud: met.dias_trabajo + met.dias_descanso,
    };
  });
}

function previewCicloPatron(patron, dias = 28) {
  const inicio = patron.fecha_inicio_ciclo || inicioAnioIso();
  const ancla = anclaConteoPatron(inicio, patron.inicio_ciclo_modo);
  const bloques = bloquesPreview(patron);
  const ciclo = Math.max(1, bloques.reduce((acc, b) => acc + b.longitud, 0));
  const out = [];
  for (let i = 0; i < dias; i++) {
    const fecha = addDaysIso(inicio, i);
    const offset = daysBetween(ancla, fecha);
    if (offset < 0) continue;
    let pos = ((offset % ciclo) + ciclo) % ciclo;
    let placed = null;
    for (const b of bloques) {
      if (pos < b.longitud) {
        placed = { b, pos };
        break;
      }
      pos -= b.longitud;
    }
    if (!placed || placed.pos >= placed.b.dias_trabajo) {
      out.push({ fecha, tipo: "descanso", label: "Descanso" });
    } else {
      out.push({
        fecha,
        tipo: "trabajo",
        label: `${placed.b.nombre} ${placed.b.dias_trabajo}×${placed.b.dias_descanso} · Día ${placed.pos + 1}`,
        turnoId: placed.b.id,
      });
    }
  }
  return out;
}

function daysBetween(isoA, isoB) {
  const a = new Date(`${isoA}T12:00:00`);
  const b = new Date(`${isoB}T12:00:00`);
  return Math.round((b - a) / 86400000);
}

/** Lunes de la semana de `iso` si modo=semana; si no, la propia fecha. */
function anclaConteoPatron(iso, modo) {
  if (modo !== "semana") return iso;
  const d = new Date(`${iso}T12:00:00`);
  const wd = (d.getDay() + 6) % 7; // 0=lunes
  d.setDate(d.getDate() - wd);
  return d.toISOString().slice(0, 10);
}

/** Orden de rotación por bloques. Migra el formato antiguo (1 id por día de trabajo). */
function normalizarSecuenciaRotacion(seqRaw, turnos, nTrabajo) {
  const ids = new Set((turnos || []).map((t) => t.id));
  let seq = (seqRaw || []).filter((id) => ids.has(id));
  const n = Math.max(1, Number(nTrabajo) || 5);
  if (seq.length === n && n > 1) {
    const collapsed = [];
    for (const tid of seq) {
      if (!collapsed.length || collapsed[collapsed.length - 1] !== tid) collapsed.push(tid);
    }
    seq = collapsed;
  }
  if (!seq.length && turnos?.length) seq = turnos.map((t) => t.id);
  return seq;
}

function ajustarSecuencia(patron, nTrabajo, turnos) {
  return normalizarSecuenciaRotacion(patron.secuencia_turnos, turnos, nTrabajo);
}

export function modoConfigDefecto() {
  return {
    modo: MODO_FIJO,
    flexible: { ...DEFAULT_FLEXIBLE },
    patron: {
      tipo: "5x2",
      dias_trabajo: 5,
      dias_descanso: 2,
      fecha_inicio_ciclo: inicioAnioIso(),
      inicio_ciclo_modo: "fecha",
      festivos_laborables: false,
      descanso_minimo_entre_jornadas_horas: 12,
      descanso_semanal_horas: 36,
      pausa_minima_minutos: 15,
      preaviso_cambio_dias: 5,
      margen_entrada_antes_minutos: 15,
      turnos: [{ id: genId(), nombre: "Mañana", horario: horarioPatronDiasDefecto(5), patron_propio: false, tramo_variable: false }],
      secuencia_turnos: [],
    },
  };
}

export function HorarioModoConfig({ config, onChange, plantillas = [] }) {
  const cfg = config || modoConfigDefecto();
  const { modo, flexible, patron } = cfg;
  const [computoCambiado, setComputoCambiado] = useState(false);
  const objetivoDiario = objetivoDiarioFlexible(flexible);
  const objetivoInusual = objetivoDiarioInusual(objetivoDiario);

  const setModo = (m) => onChange({ ...cfg, modo: m });

  const setFlexible = (patch) => onChange({ ...cfg, flexible: { ...flexible, ...patch } });

  const setPatron = (patch) => {
    const next = { ...patron, ...patch };
    const nTrab = Number(next.dias_trabajo) || 5;
    if (patch.dias_trabajo != null && patch.turnos == null) {
      next.turnos = (next.turnos || []).map((t) => {
        if (t.patron_propio) return t;
        const horario = normalizarHorarioPatronDias(t.horario, nTrab);
        return { ...t, horario: t.tramo_variable ? horario : unificarTramoPatron(horario, nTrab) };
      });
    }
    if (patch.dias_trabajo != null || patch.turnos != null || patch.secuencia_turnos != null) {
      next.secuencia_turnos = ajustarSecuencia(next, nTrab, next.turnos || []);
    }
    onChange({ ...cfg, patron: next });
  };

  const aplicarTipoPatron = (tipo) => {
    const preset = PATRONES_TURNO.find((p) => p.id === tipo) || PATRONES_TURNO[1];
    setPatron({
      tipo,
      dias_trabajo: preset.dias_trabajo,
      dias_descanso: preset.dias_descanso,
    });
  };

  const setTurnoPatron = (idx, patch) => {
    const turnos = patron.turnos.map((t, i) => {
      if (i !== idx) return t;
      let next = { ...t, ...patch };
      const met = metricasTurnoPatron(next, patron);
      if (
        patch.horario !== undefined
        || patch.dias_trabajo != null
        || patch.patron_propio != null
        || patch.tipo != null
      ) {
        next.horario = normalizarHorarioPatronDias(next.horario, met.dias_trabajo);
      }
      if (patch.tramo_variable === false || (next.tramo_variable === false && patch.horario === undefined && (patch.dias_trabajo != null || patch.patron_propio != null || patch.tipo != null))) {
        next.horario = unificarTramoPatron(next.horario, met.dias_trabajo);
        next.tramo_variable = false;
      }
      return next;
    });
    setPatron({ turnos });
  };

  const addTurnoPatron = () => {
    const id = genId();
    const turnos = [
      ...patron.turnos,
      {
        id,
        nombre: `Turno ${patron.turnos.length + 1}`,
        horario: horarioPatronDiasDefecto(patron.dias_trabajo),
        patron_propio: false,
        tramo_variable: false,
      },
    ];
    const seq = ajustarSecuencia(patron, patron.dias_trabajo, patron.turnos);
    setPatron({ turnos, secuencia_turnos: [...seq, id] });
  };

  const removeTurnoPatron = (idx) => {
    if (patron.turnos.length <= 1) return;
    const removed = patron.turnos[idx];
    const turnos = patron.turnos.filter((_, i) => i !== idx);
    setPatron({
      turnos,
      secuencia_turnos: ajustarSecuencia(
        { ...patron, secuencia_turnos: (patron.secuencia_turnos || []).filter((id) => id !== removed.id) },
        patron.dias_trabajo,
        turnos
      ),
    });
  };

  const moverSecuencia = (idx, dir) => {
    const seq = [...ajustarSecuencia(patron, patron.dias_trabajo, patron.turnos)];
    const j = idx + dir;
    if (j < 0 || j >= seq.length) return;
    [seq[idx], seq[j]] = [seq[j], seq[idx]];
    setPatron({ secuencia_turnos: seq });
  };

  const cicloPreview = bloquesPreview(patron).reduce((acc, b) => acc + b.longitud, 0);
  const previewDias = Math.min(42, Math.max(14, cicloPreview || 14));

  const preview = useMemo(
    () => (modo === MODO_PATRON_TURNOS ? previewCicloPatron(patron, previewDias) : []),
    [modo, patron, previewDias]
  );

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">Tipo de horario</Label>
        <div className="grid gap-2">
          {MODOS_HORARIO.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setModo(m.id)}
              className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                modo === m.id ? "border-indigo-400 bg-indigo-50" : "border-zinc-200 hover:bg-zinc-50"
              }`}
              data-testid={`horario-modo-${m.id}`}
            >
              <p className="text-sm font-medium text-zinc-900">{m.label}</p>
              <p className="text-xs text-zinc-500">{m.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {modo === MODO_FLEXIBLE && (
        <div className="space-y-3 rounded-lg border border-amber-100 bg-amber-50/40 p-4">
          <p className="text-xs text-amber-900">
            Ventana diaria sin incidencias por retraso. Las horas se acumulan como normales hasta el objetivo
            semanal/mensual; solo el exceso sobre ese cómputo requiere autorización como horas extra.
            Si olvidó la salida, el cierre automático es a las 12 h desde la entrada. Aviso a encargados solo
            si supera 9 h con fichajes reales.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Margen entrada (min. antes de ventana)</Label>
              <Input
                type="number"
                min={0}
                max={180}
                value={flexible.margen_entrada_antes_minutos}
                onChange={(e) => setFlexible({ margen_entrada_antes_minutos: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Cómputo de horas</Label>
              <Select
                value={flexible.computo}
                onValueChange={(v) => {
                  if (v !== flexible.computo) setComputoCambiado(true);
                  setFlexible({ computo: v });
                }}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="semana">Por semana</SelectItem>
                  <SelectItem value="mes">Por mes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {flexible.computo === "semana" ? (
              <div className="space-y-1.5">
                <Label className="text-xs">Horas objetivo / semana</Label>
                <Input
                  type="number"
                  min={1}
                  step={0.5}
                  value={flexible.horas_objetivo_semana}
                  onChange={(e) => {
                    setComputoCambiado(false);
                    setFlexible({ horas_objetivo_semana: Number(e.target.value) || 0 });
                  }}
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Horas objetivo / mes</Label>
                <Input
                  type="number"
                  min={1}
                  step={0.5}
                  value={flexible.horas_objetivo_mes}
                  onChange={(e) => {
                    setComputoCambiado(false);
                    setFlexible({ horas_objetivo_mes: Number(e.target.value) || 0 });
                  }}
                />
              </div>
            )}
            <div className="col-span-2 space-y-1" data-testid="objetivo-diario-flexible">
              {computoCambiado && (
                <p className="text-xs text-amber-800">
                  Has cambiado el tipo de cómputo: revisa las horas, el valor de {flexible.computo === "mes" ? "la semana" : "el mes"} no se convierte solo.
                </p>
              )}
              <p
                className={`rounded-md border px-2.5 py-1.5 text-xs ${
                  objetivoInusual
                    ? "border-amber-300 bg-amber-50 text-amber-900"
                    : "border-emerald-200 bg-emerald-50/60 text-emerald-900"
                }`}
              >
                {objetivoInusual ? (
                  <>
                    Con {flexible.computo === "mes" ? `${flexible.horas_objetivo_mes} h al mes` : `${flexible.horas_objetivo_semana} h a la semana`} salen{" "}
                    <strong>{fmtHorasDia(objetivoDiario)} al día</strong>. ¿Es lo que quieres? Para 8 h al día suelen
                    ser 40 h a la semana o 160 h al mes. Puedes guardarlo igualmente.
                  </>
                ) : (
                  <>
                    Equivale a <strong>{fmtHorasDia(objetivoDiario)} al día</strong> ({DIAS_LAB_FLEXIBLE_ESTIMADO} días laborables).
                  </>
                )}
              </p>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs">Autocierre si olvidó salida (h desde entrada)</Label>
              <Input
                type="number"
                min={1}
                max={16}
                step={0.5}
                value={flexible.autocierre_horas_trabajo ?? 12}
                onChange={(e) => setFlexible({ autocierre_horas_trabajo: Number(e.target.value) || 12 })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Aviso encargados si supera (h, fichaje real)</Label>
              <Input
                type="number"
                min={1}
                max={16}
                step={0.5}
                value={flexible.alerta_horas_jornada ?? 9}
                onChange={(e) => setFlexible({ alerta_horas_jornada: Number(e.target.value) || 9 })}
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border bg-white px-3 py-2">
            <div>
              <Label className="text-sm">Cierre automático de jornada</Label>
              <p className="text-[11px] text-zinc-500">
                Si no hay salida, a las {flexible.autocierre_horas_trabajo ?? 12} h desde la entrada
              </p>
            </div>
            <Switch
              checked={!!flexible.autocierre_activo}
              onCheckedChange={(v) => setFlexible({ autocierre_activo: v })}
            />
          </div>
        </div>
      )}

      {modo === MODO_PATRON_TURNOS && (
        <div className="space-y-3 rounded-lg border border-violet-100 bg-violet-50/30 p-4">
          <div className="flex gap-2 text-xs text-violet-900">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Cada turno de la rotación puede usar el patrón general o uno distinto. Por ejemplo, el primero
              en 5×2 (5 días de trabajo y 2 libres) y el segundo en 3×2. En cada turno indica si el tramo
              horario es el mismo todos los días de trabajo o si varía según el día del bloque.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Patrón de rotación</Label>
              <Select value={patron.tipo} onValueChange={aplicarTipoPatron}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PATRONES_TURNO.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {patron.tipo === "personalizado" && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Días de trabajo</Label>
                  <Input type="number" min={1} max={14} value={patron.dias_trabajo} onChange={(e) => setPatron({ dias_trabajo: Number(e.target.value) || 1 })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Días de descanso</Label>
                  <Input type="number" min={1} max={14} value={patron.dias_descanso} onChange={(e) => setPatron({ dias_descanso: Number(e.target.value) || 1 })} />
                </div>
              </>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Fecha de inicio / vigencia</Label>
              <Input type="date" value={patron.fecha_inicio_ciclo} onChange={(e) => setPatron({ fecha_inicio_ciclo: e.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Cómo empieza el ciclo</Label>
              <Select
                value={patron.inicio_ciclo_modo || "fecha"}
                onValueChange={(v) => setPatron({ inicio_ciclo_modo: v })}
              >
                <SelectTrigger data-testid="patron-inicio-ciclo-modo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fecha">Desde la fecha indicada (días corridos)</SelectItem>
                  <SelectItem value="semana">Alineado a la semana (p. ej. 5×2 = L–V / S–D)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500">
                {patron.inicio_ciclo_modo === "semana"
                  ? "El ciclo se ancla al lunes de esa semana. Si la vigencia empieza en jueves (p. ej. 1/10/2026), solo se trabaja J y V y se descansa S y D."
                  : "Cuenta N días de trabajo y M de descanso a partir de la fecha, sin importar el día de la semana."}
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border bg-white px-3 py-2 sm:col-span-2">
              <div>
                <Label className="text-sm">Festivos laborables</Label>
                <p className="text-[11px] text-zinc-500">
                  Si está activo, un festivo que cae en día de patrón se trabaja. Si no, el festivo es descanso.
                </p>
              </div>
              <Switch
                checked={!!patron.festivos_laborables}
                onCheckedChange={(v) => setPatron({ festivos_laborables: v })}
                data-testid="patron-festivos-laborables"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Margen de entrada (minutos antes del turno)</Label>
              <Input
                type="number"
                min={0}
                max={180}
                value={patron.margen_entrada_antes_minutos ?? 15}
                onChange={(e) =>
                  setPatron({ margen_entrada_antes_minutos: Number(e.target.value) || 0 })
                }
                data-testid="patron-margen-entrada"
              />
              <p className="text-[11px] text-zinc-500">
                Si el trabajador ficha antes de ese margen, se avisa a administrador y encargado.
                Al entrar, la hora de salida del día se autoajusta manteniendo la duración del turno.
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">Turnos del patrón</Label>
            {patron.turnos.map((t, idx) => {
              const met = metricasTurnoPatron(t, patron);
              return (
              <div key={t.id} className="rounded-lg border bg-white p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <Input
                    className="max-w-[200px]"
                    value={t.nombre}
                    onChange={(e) => setTurnoPatron(idx, { nombre: e.target.value })}
                    placeholder="Nombre del turno"
                  />
                  {patron.turnos.length > 1 && (
                    <button type="button" onClick={() => removeTurnoPatron(idx)} className="rounded p-1 text-zinc-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Patrón de este turno</Label>
                  <Select
                    value={t.patron_propio ? (t.tipo || "personalizado") : "mismo"}
                    onValueChange={(v) => {
                      if (v === "mismo") {
                        setTurnoPatron(idx, { patron_propio: false });
                        return;
                      }
                      const preset = PATRONES_TURNO.find((p) => p.id === v);
                      setTurnoPatron(idx, {
                        patron_propio: true,
                        tipo: v,
                        dias_trabajo: preset?.dias_trabajo ?? t.dias_trabajo,
                        dias_descanso: preset?.dias_descanso ?? t.dias_descanso,
                      });
                    }}
                  >
                    <SelectTrigger data-testid={`patron-turno-${idx}-patron`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mismo">
                        Mismo patrón ({patron.dias_trabajo}×{patron.dias_descanso})
                      </SelectItem>
                      {PATRONES_TURNO.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {t.patron_propio && t.tipo === "personalizado" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Días de trabajo</Label>
                      <Input
                        type="number"
                        min={1}
                        max={14}
                        value={t.dias_trabajo}
                        onChange={(e) => setTurnoPatron(idx, { dias_trabajo: Number(e.target.value) || 1 })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Días de descanso</Label>
                      <Input
                        type="number"
                        min={1}
                        max={14}
                        value={t.dias_descanso}
                        onChange={(e) => setTurnoPatron(idx, { dias_descanso: Number(e.target.value) || 1 })}
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs">Tramo horario</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setTurnoPatron(idx, { tramo_variable: false })}
                      data-testid={`patron-turno-${idx}-tramo-mismo`}
                      className={`rounded-lg border px-3 py-2 text-left text-xs ${
                        !t.tramo_variable ? "border-indigo-400 bg-indigo-50 text-indigo-950" : "border-zinc-200 text-zinc-600"
                      }`}
                    >
                      <span className="block font-medium">Mismo tramo</span>
                      <span className="text-[11px] opacity-80">Igual en los {met.dias_trabajo} días</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTurnoPatron(idx, { tramo_variable: true })}
                      data-testid={`patron-turno-${idx}-tramo-varia`}
                      className={`rounded-lg border px-3 py-2 text-left text-xs ${
                        t.tramo_variable ? "border-indigo-400 bg-indigo-50 text-indigo-950" : "border-zinc-200 text-zinc-600"
                      }`}
                    >
                      <span className="block font-medium">Varía</span>
                      <span className="text-[11px] opacity-80">Horario distinto cada día</span>
                    </button>
                  </div>
                </div>
                <HorarioPatronDiasEditor
                  horario={t.horario}
                  onChange={(h) => setTurnoPatron(idx, { horario: h })}
                  diasTrabajo={met.dias_trabajo}
                  prefix={`patron-${idx}`}
                  tramoVariable={!!t.tramo_variable}
                />
              </div>
              );
            })}
            <Button type="button" variant="outline" size="sm" onClick={addTurnoPatron} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Añadir turno
            </Button>
          </div>
          {patron.turnos.length >= 1 && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
                Orden de rotación de turnos
              </Label>
              <p className="text-[11px] text-zinc-500">
                Los turnos se encadenan: el bloque completo del primero (trabajo y descanso), después el del
                segundo, y así hasta volver al primero. Si el segundo tiene otro patrón, su bloque dura lo que
                indiques (p. ej. 5×2 y luego 3×2).
              </p>
              <ol className="space-y-1.5">
                {ajustarSecuencia(patron, patron.dias_trabajo, patron.turnos).map((tid, i, seq) => {
                  const t = patron.turnos.find((x) => x.id === tid);
                  const met = metricasTurnoPatron(t || {}, patron);
                  return (
                    <li
                      key={`${tid}-${i}`}
                      className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm"
                    >
                      <span className="w-6 text-xs font-semibold text-violet-600">{i + 1}.</span>
                      <span className="flex-1 font-medium text-zinc-900">{t?.nombre || tid}</span>
                      <span className="text-[11px] text-zinc-500">
                        {met.dias_trabajo}×{met.dias_descanso}
                        {t?.patron_propio ? " · patrón propio" : ""}
                      </span>
                      <div className="flex gap-0.5">
                        <button
                          type="button"
                          disabled={i === 0}
                          onClick={() => moverSecuencia(i, -1)}
                          className="rounded px-1.5 py-0.5 text-xs text-zinc-500 hover:bg-zinc-100 disabled:opacity-30"
                          aria-label="Subir"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          disabled={i === seq.length - 1}
                          onClick={() => moverSecuencia(i, 1)}
                          className="rounded px-1.5 py-0.5 text-xs text-zinc-500 hover:bg-zinc-100 disabled:opacity-30"
                          aria-label="Bajar"
                        >
                          ↓
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ol>
              {preview.length > 0 && (
                <div className="rounded-lg border border-violet-100 bg-white p-3" data-testid="patron-preview-ciclo">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-violet-700">
                    Vista previa desde {patron.fecha_inicio_ciclo} (ciclo completo)
                  </p>
                  <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                    {preview.map((d) => (
                      <li
                        key={d.fecha}
                        className={`flex items-center justify-between rounded px-2 py-1 text-xs ${
                          d.tipo === "descanso" ? "bg-zinc-50 text-zinc-500" : "bg-violet-50 text-violet-900"
                        }`}
                      >
                        <span>{fmtDiaCorto(d.fecha)}</span>
                        <span className="font-medium">{d.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
